import {
  dependentsOfDelete,
  questionRefLabel,
  type QuestionDependent,
  type QuestionnaireDraft,
} from "@digital-twins/shared-types";

/** How a dependent uses the question, as the delete confirm names it. */
export type DeleteDependentLink = "condition" | "route" | "choicesFrom" | "followUp";

export interface DeleteDependent {
  /** The dependent question's number on screen ("Q5"). */
  label: string;
  link: DeleteDependentLink;
}

function linkOf(dependent: QuestionDependent): DeleteDependentLink {
  if (dependent.kind === "choices_from_source") return "choicesFrom";
  if (dependent.kind === "follow_up_parent") return "followUp";
  if (dependent.kind === "skip_target") return "route";
  return dependent.leafPath?.startsWith("routingRule") ? "route" : "condition";
}

/**
 * What deleting `questionId` would clear, from the one detector
 * (`dependentsOfDelete`): one line per dependent question and link, in
 * questionnaire order. The delete clears exactly these (`clearDependents`).
 */
export function deleteDependents(
  draft: QuestionnaireDraft | null | undefined,
  questionId: string,
): Array<DeleteDependent> {
  const questions = draft?.researchQuestions ?? [];
  const deleted = questions.find((question) => question.id === questionId);
  if (!deleted) return [];
  const indexByKey = new Map(questions.map((question, index) => [question.stableKey, index]));
  const seen = new Set<string>();
  const lines: Array<DeleteDependent> = [];
  for (const dependent of dependentsOfDelete(questions, deleted.stableKey)) {
    const index = indexByKey.get(dependent.questionStableKey);
    const link = linkOf(dependent);
    const key = `${dependent.questionStableKey}:${link}`;
    if (index === undefined || seen.has(key)) continue;
    seen.add(key);
    lines.push({ label: questionRefLabel(index), link });
  }
  return lines;
}
